import { ROOT_ID, formatLength, snapDrag, type Rect, type Scene, type SnapLine } from '@flobi/artwork-core';
import type { IconName } from '../chrome/icons';
import { ARTBOARD_PRESETS, presetMenu, saysWayUp, type PresetInfo } from '../artboardPresets';
import type { Point, RailButton, Tool, ToolContext } from './types';

/**
 * The Artboard tool: pages, made and arranged.
 *
 *   — a CLICK on the canvas makes one of the chosen size, centred where you clicked;
 *   — a DRAG makes one exactly the size you drag;
 *   — a press on an artboard takes hold of it: inside moves it (its artwork goes with it),
 *     a corner sizes it (its artwork stays where it is on the page);
 *   — Return, or Add in the strip, puts one of the chosen size beside the others.
 *
 * The size is ALWAYS the one chosen here — a preset from the strip, or the box dragged.
 * It is never taken from whichever artboard was edited last. That is not an oversight:
 * Adobe's US 8,667,406 (to about 2031) claims creating a new artboard sized from the
 * artboard whose content was edited most recently. Do not add "same size as the one I was
 * working on" as an automatic default; a person who wants that can duplicate a board.
 */

/** Less than this many pixels of travel is a click, not a drag. */
const LEAST = 6;
/** How close to a corner, in pixels, a press has to land to take hold of it. */
const GRIP = 9;
/** How far, in pixels, an edge is pulled onto another board's edge, a guide or the grid. */
const SNAP_REACH = 6;
/** The gutter between boards laid out in a row, in document units. */
const GAP = 80;

type Grip = 'move' | 'nw' | 'ne' | 'se' | 'sw';

const WHITE = [{ type: 'solid', color: { r: 1, g: 1, b: 1, a: 1 }, visible: true, opacity: 1, blendMode: 'normal' }];

function between(a: Point, b: Point): Rect {
  return { x: Math.min(a.x, b.x), y: Math.min(a.y, b.y), width: Math.abs(b.x - a.x), height: Math.abs(b.y - a.y) };
}

function cornersOf(box: Rect): Record<Exclude<Grip, 'move'>, Point> {
  return {
    nw: { x: box.x, y: box.y },
    ne: { x: box.x + box.width, y: box.y },
    se: { x: box.x + box.width, y: box.y + box.height },
    sw: { x: box.x, y: box.y + box.height },
  };
}

const ACROSS = { nw: 'se', ne: 'sw', se: 'nw', sw: 'ne' } as const;

/**
 * Where a new artboard goes in the stack: behind the loose art on the page.
 *
 * It went to the front with an opaque white page, so drawing first and adding a board
 * over it made the drawing look deleted (QA 2026-09-24, artboards-F3). Behind the
 * bottom-most layer that is not an artboard, the drawing stays in view on top of it.
 * Nothing is moved into the board; that stays a person's choice.
 */
export function behindLooseArt(scene: Scene): { position?: { before: string } } {
  const loose = scene.childrenOf(ROOT_ID).find((id) => scene.get(id)?.type !== 'artboard');
  return loose ? { position: { before: loose } } : {};
}

/**
 * Where a board added "beside the others" goes: past the right-most board by the gap, on
 * the top row. The ONE placement every Add uses — the tool's Return and Add, the page
 * panel's Add and the title menu's New Artboard. The canvas's own copy read a real top row
 * at y = 0 as "not set yet" and lined the board up with a lower row instead
 * (QA 2026-09-24, artboards-F7).
 */
export function besideBoards(scene: Scene): { x: number; y: number } {
  let right = -Infinity;
  let top = Infinity;
  for (const id of scene.childrenOf(ROOT_ID)) {
    const node = scene.get(id) as { type?: string; width?: number; transform?: number[] } | undefined;
    if (node?.type !== 'artboard' || !node.transform) continue;
    right = Math.max(right, node.transform[4]! + (node.width ?? 0));
    top = Math.min(top, node.transform[5]!);
  }
  return Number.isFinite(right) ? { x: right + GAP, y: top } : { x: 0, y: 0 };
}

export function artboardTool(): Tool {
  let preset: PresetInfo = ARTBOARD_PRESETS.find((one) => one.id === 'a4')!;
  /** Which way up the next board is; set from the preset whenever a preset is chosen. */
  let landscape = preset.width > preset.height;
  let drawing: { from: Point; to: Point } | null = null;
  let held: { id: string; grip: Grip; from: Point; was: Rect; kids: { id: string; transform: number[] }[] } | null = null;
  /** Where the pointer is over empty canvas, for the outline of what a click would make. */
  let hover: Point | null = null;
  /** The lines a drag has just lined up on, drawn while it holds. */
  let lines: readonly SnapLine[] = [];
  /** The board being sized has been dragged down to nothing on one side. */
  let collapsed = false;

  /**
   * A box snapped as the Select tool snaps a shape: its edges and its middle onto other
   * boards' edges and middles, the guides, and the grid — whichever the snapping toggles
   * allow. Moving a board used to snap the POINTER to the grid and carry the board by
   * however far the pointer went, so a board that started off the grid stayed off it, and
   * two boards could never be lined up edge to edge however carefully they were dragged.
   */
  const snapped = (ctx: ToolContext, box: Rect, ignore: string): Rect => {
    if (ctx.modifiers.meta) {
      lines = [];
      return box;
    }
    const targets = ctx.snap.smart ? boards(ctx).filter((one) => one.id !== ignore).map((one) => one.box) : [];
    const snap = snapDrag(box, { targets, guides: ctx.guides }, ctx.snap, SNAP_REACH * ctx.unitsPerPixel);
    lines = snap.lines;
    ctx.catchGuides(snap.onGuides ?? []);
    return { ...box, x: box.x + snap.dx, y: box.y + snap.dy };
  };

  /** A corner being dragged, snapped the same way: a box with no size, lined up by its edges. */
  const snappedCorner = (ctx: ToolContext, at: Point, ignore: string): Point => {
    const box = snapped(ctx, { x: at.x, y: at.y, width: 0, height: 0 }, ignore);
    return { x: box.x, y: box.y };
  };

  /** The chosen size, the chosen way up. */
  const size = (): { width: number; height: number } => {
    const long = Math.round(Math.max(preset.width, preset.height));
    const short = Math.round(Math.min(preset.width, preset.height));
    return landscape ? { width: long, height: short } : { width: short, height: long };
  };

  const boards = (ctx: ToolContext): { id: string; box: Rect }[] =>
    ctx.scene.childrenOf(ROOT_ID).flatMap((id) => {
      const node = ctx.node(id) as { type?: string; width?: number; height?: number; transform?: number[] } | undefined;
      if (node?.type !== 'artboard' || !node.transform) return [];
      return [{ id, box: { x: node.transform[4]!, y: node.transform[5]!, width: node.width ?? 0, height: node.height ?? 0 } }];
    });

  /** The board under a point, topmost first, and which part of it: a corner, or the rest. */
  const under = (ctx: ToolContext, at: Point): { id: string; grip: Grip; box: Rect } | null => {
    const reach = GRIP * ctx.unitsPerPixel;
    const all = boards(ctx);
    for (let i = all.length - 1; i >= 0; i--) {
      const { id, box } = all[i]!;
      for (const [grip, corner] of Object.entries(cornersOf(box)) as [Exclude<Grip, 'move'>, Point][]) {
        if (Math.abs(at.x - corner.x) <= reach && Math.abs(at.y - corner.y) <= reach) return { id, grip, box };
      }
    }
    for (let i = all.length - 1; i >= 0; i--) {
      const { id, box } = all[i]!;
      if (at.x >= box.x && at.x <= box.x + box.width && at.y >= box.y && at.y <= box.y + box.height) return { id, grip: 'move', box };
    }
    return null;
  };

  const make = (ctx: ToolContext, box: Rect, name: string, presetId?: string): void => {
    const rounded = { x: Math.round(box.x), y: Math.round(box.y), width: Math.max(1, Math.round(box.width)), height: Math.max(1, Math.round(box.height)) };
    const result = ctx.edit([
      {
        op: 'create',
        ref: 'board',
        parentId: ROOT_ID,
        ...behindLooseArt(ctx.scene),
        node: {
          type: 'artboard',
          name,
          ...(presetId ? { preset: presetId } : {}),
          width: rounded.width,
          height: rounded.height,
          transform: [1, 0, 0, 1, rounded.x, rounded.y],
          fills: WHITE,
        },
      },
    ]);
    if (!result.ok || !result.created.board) return;
    ctx.setSelection([result.created.board]);
    ctx.stopCapturing();
    ctx.reveal(rounded);
    ctx.refresh();
  };

  /** One of the chosen size at the end of the row, as the page panel's Add does. */
  const addBeside = (ctx: ToolContext): void => {
    const { width, height } = size();
    make(ctx, { ...besideBoards(ctx.scene), width, height }, preset.name, preset.id);
  };

  /**
   * Moves or sizes a board. Sizing from the top or left moves the board's origin, and a
   * board's artwork is drawn from its origin — so it is moved back by the same amount, and
   * stays where it is on the page, as it does when a page is resized anywhere else.
   */
  const write = (ctx: ToolContext, box: Rect): void => {
    if (!held) return;
    // A corner dropped onto the opposite corner, or onto its row or column, has no size on
    // that side: noted here and refused on release (QA 2026-09-24, artboards-F19).
    collapsed = Math.round(box.width) < 1 || Math.round(box.height) < 1;
    const rounded = { x: Math.round(box.x), y: Math.round(box.y), width: Math.max(1, Math.round(box.width)), height: Math.max(1, Math.round(box.height)) };
    const ops: unknown[] = [{ op: 'set', id: held.id, props: { width: rounded.width, height: rounded.height, transform: [1, 0, 0, 1, rounded.x, rounded.y] } }];
    if (held.grip !== 'move') {
      const dx = rounded.x - held.was.x;
      const dy = rounded.y - held.was.y;
      for (const kid of held.kids) {
        const [a, b, c, d, e, f] = kid.transform;
        // force: this keeps the layer exactly where it is on the page, locked or not. A
        // locked layer used to refuse the batch, and the board would not resize at all
        // (QA 2026-09-24, artboards-F1).
        if (dx !== 0 || dy !== 0) ops.push({ op: 'set', id: kid.id, props: { transform: [a, b, c, d, e! - dx, f! - dy] }, force: true });
      }
    }
    ctx.edit(ops);
  };

  /** A board being moved or sized goes back where it was, and its artwork with it. */
  const putBack = (ctx: ToolContext): void => {
    if (!held || !ctx.node(held.id)) return;
    const was = held.was;
    const ops: unknown[] = [{ op: 'set', id: held.id, props: { width: was.width, height: was.height, transform: [1, 0, 0, 1, was.x, was.y] }, force: true }];
    // Only a corner moves the artwork back against the board; a move carries it along.
    if (held.grip !== 'move') for (const kid of held.kids) if (ctx.node(kid.id)) ops.push({ op: 'set', id: kid.id, props: { transform: kid.transform }, force: true });
    ctx.edit(ops);
  };

  /**
   * "Artboard N" with the first N no board is called yet. The count alone named a new
   * board after one that already existed once an earlier board had been deleted
   * (QA 2026-09-24, artboards-F18).
   */
  const nextName = (ctx: ToolContext): string => {
    const taken = new Set(boards(ctx).map(({ id }) => (ctx.node(id) as { name?: string } | undefined)?.name));
    let n = boards(ctx).length + 1;
    while (taken.has(`Artboard ${n}`)) n++;
    return `Artboard ${n}`;
  };

  const choose = (ctx: ToolContext, next: PresetInfo): void => {
    preset = next;
    landscape = next.width > next.height;
    ctx.railChanged();
    ctx.refresh();
  };

  /** The boards chosen now, which Portrait and Landscape turn. */
  const chosen = (ctx: ToolContext): { id: string; box: Rect }[] => boards(ctx).filter(({ id }) => ctx.selection.includes(id));

  /**
   * Portrait or Landscape. With boards chosen it turns them on their side — width and
   * height swapped, the top-left corner kept, and their artwork moved by half the change
   * so what sat in the middle of the page still does. It used to set only the way up of
   * the NEXT board, so picking Landscape with an A4 chosen did nothing to it (Farah,
   * 27 Oct). Either way the next board is made that way up too.
   */
  const turn = (ctx: ToolContext, wide: boolean): void => {
    landscape = wide;
    const ops: unknown[] = [];
    for (const { id, box } of chosen(ctx)) {
      if (Math.round(box.width) === Math.round(box.height) || box.width > box.height === wide) continue;
      ops.push({ op: 'set', id, props: { width: box.height, height: box.width } });
      const dx = (box.height - box.width) / 2;
      const dy = (box.width - box.height) / 2;
      for (const kid of ctx.scene.childrenOf(id)) {
        const transform = (ctx.node(kid) as { transform?: number[] } | undefined)?.transform;
        if (!transform) continue;
        const [a, b, c, d, e, f] = transform;
        // force, as a corner drag does: a locked layer keeps its place on the page.
        ops.push({ op: 'set', id: kid, props: { transform: [a, b, c, d, e! + dx, f! + dy] }, force: true });
      }
    }
    if (ops.length > 0) {
      ctx.edit(ops);
      ctx.stopCapturing();
    }
    ctx.railChanged();
    ctx.refresh();
  };

  return {
    id: 'artboard',

    activate(ctx) {
      ctx.setCursor('crosshair');
    },

    deactivate(ctx) {
      // Put down mid-drag, the drag stands where it got to — the canvas closes its undo
      // step as the tool changes — and its guide lines go with it rather than staying on
      // the glass (QA 2026-09-24, artboards-F11).
      drawing = null;
      held = null;
      hover = null;
      lines = [];
      ctx.catchGuides([]);
      ctx.setCursor(null);
    },

    /**
     * A board being moved or sized keeps its undo step open until it is let go, so that
     * Escape — which puts it back — takes the whole drag back as nothing rather than
     * leaving the half-move behind as a step of its own.
     */
    holdsStep: () => held !== null || drawing !== null,

    /**
     * Off the canvas — onto the toolbar, the dock, a ruler — the outline of what a click
     * would make goes; it used to stay where the hand left it (QA 2026-09-24, artboards-F17).
     */
    onPointerLeave(ctx) {
      if (!hover) return;
      hover = null;
      ctx.refresh();
    },

    onPointerDown(_event, rawWorld, ctx) {
      hover = null;
      const found = under(ctx, rawWorld);
      if (found) {
        const kids = ctx.scene.childrenOf(found.id).flatMap((id) => {
          const transform = (ctx.node(id) as { transform?: number[] } | undefined)?.transform;
          return transform ? [{ id, transform: [...transform] }] : [];
        });
        held = { id: found.id, grip: found.grip, from: rawWorld, was: found.box, kids };
        collapsed = false;
        ctx.setSelection([found.id]);
        ctx.refresh();
        return true;
      }
      const at = snappedCorner(ctx, rawWorld, '');
      drawing = { from: at, to: at };
      ctx.setSelection([]);
      return true;
    },

    onPointerMove(event, rawWorld, ctx) {
      if (event.buttons === 0) {
        const found = under(ctx, rawWorld);
        ctx.setCursor(found ? (found.grip === 'move' ? 'move' : found.grip === 'nw' || found.grip === 'se' ? 'nwse-resize' : 'nesw-resize') : 'crosshair');
        const was = hover;
        hover = found ? null : rawWorld;
        // Redrawn only when the outline would visibly move: a hand crossing the canvas asks
        // for a frame at every pixel otherwise.
        if (!was !== !hover || (was && hover && Math.hypot(was.x - hover.x, was.y - hover.y) > 2 * ctx.unitsPerPixel)) ctx.refresh();
        else if (hover) hover = was;
        return;
      }
      if (held) {
        const dx = rawWorld.x - held.from.x;
        const dy = rawWorld.y - held.from.y;
        const was = held.was;
        if (held.grip === 'move') {
          write(ctx, snapped(ctx, { ...was, x: was.x + dx, y: was.y + dy }, held.id));
        } else {
          const corners = cornersOf(was);
          const fixed = corners[ACROSS[held.grip]];
          write(ctx, between(fixed, snappedCorner(ctx, { x: corners[held.grip].x + dx, y: corners[held.grip].y + dy }, held.id)));
        }
        ctx.refresh();
        return;
      }
      if (!drawing) return;
      drawing = { from: drawing.from, to: snappedCorner(ctx, rawWorld, '') };
      ctx.refresh();
    },

    onPointerUp(_event, rawWorld, ctx) {
      lines = [];
      ctx.catchGuides([]);
      if (held) {
        // A board sized down to nothing on one side is refused: it goes back as it was,
        // rather than staying a hairline or 1×1 page (QA 2026-09-24, artboards-F19).
        if (collapsed && held.grip !== 'move') putBack(ctx);
        held = null;
        collapsed = false;
        ctx.stopCapturing();
        ctx.refresh();
        return;
      }
      if (!drawing) return;
      const from = drawing.from;
      const to = drawing.to;
      drawing = null;
      // A drag that is flat on either axis makes no board: 6 px of travel along one line
      // used to make a hairline page (QA 2026-09-24, artboards-F19).
      const flat = Math.round(Math.abs(to.x - from.x)) < 1 || Math.round(Math.abs(to.y - from.y)) < 1;
      const travelled = Math.hypot(to.x - from.x, to.y - from.y) / ctx.unitsPerPixel;
      if (travelled < LEAST) {
        // A click: the chosen size, centred on it.
        const { width, height } = size();
        make(ctx, snapped(ctx, { x: from.x - width / 2, y: from.y - height / 2, width, height }, ''), preset.name, preset.id);
        lines = [];
        ctx.catchGuides([]);
        return;
      }
      if (flat) {
        ctx.refresh();
        return;
      }
      make(ctx, between(from, to), nextName(ctx));
    },

    onKeyDown(event, ctx) {
      // Escape mid-drag gives the drag up: a board being moved or sized goes back where it
      // was, with its artwork, and a new one being dragged out is not made. It used to go on
      // regardless, and only the selection was cleared (QA 2026-09-24, artboards-F11).
      if (event.key === 'Escape' && (held || drawing)) {
        event.preventDefault();
        putBack(ctx);
        held = null;
        drawing = null;
        lines = [];
        ctx.catchGuides([]);
        ctx.stopCapturing();
        ctx.refresh();
        return true;
      }
      // Return adds a board only when it was pressed at the canvas, with nothing else in
      // focus: from a rename field, a menu or a panel it belongs to them, and it used to add
      // boards nobody asked for (QA 2026-09-24, artboards-F4).
      const from = event.target as { tagName?: string } | null;
      const atCanvas = !from || from === document.body || from === document.documentElement || from.tagName === 'CANVAS';
      if (event.key === 'Enter' && atCanvas && !event.defaultPrevented) {
        addBeside(ctx);
        return true;
      }
      return false;
    },

    rail(ctx) {
      // With boards chosen the buttons say which way up the first one really is, and are
      // off only when every one is square; with none, the next board's way up.
      const picked = chosen(ctx).map(({ box }) => box);
      const isSquare = (box: { width: number; height: number }): boolean => Math.round(box.width) === Math.round(box.height);
      const first = picked.find((box) => !isSquare(box));
      const square = picked.length > 0 ? !first : isSquare(preset);
      const wide = first ? first.width > first.height : landscape;
      const ways: RailButton[] = [
        { id: 'portrait', icon: 'portrait' as IconName, title: 'Portrait — taller than it is wide', pressed: !square && !wide, disabled: square, run: () => turn(ctx, false) },
        { id: 'landscape', icon: 'landscape' as IconName, title: 'Landscape — wider than it is tall', pressed: !square && wide, disabled: square, run: () => turn(ctx, true) },
      ];
      const add: RailButton[] = [
        { id: 'add', icon: 'add-artboard' as IconName, title: 'Add Artboard — one this size beside the others (Return)', run: () => addBeside(ctx) },
      ];
      return {
        top: {
          kind: 'menu' as const,
          icon: 'artboard' as IconName,
          title: 'Size — what a click makes; drag for any size',
          value: `${preset.name} · ${saysWayUp(preset, wide)}`,
          items: presetMenu((next) => choose(ctx, next), preset.id),
        },
        middle: { kind: 'buttons' as const, items: ways },
        bottom: { kind: 'buttons' as const, items: add },
      };
    },

    draw(overlay, ctx) {
      for (const line of lines) {
        if (line.axis === 'x') overlay.guide({ x: line.at, y: line.from }, { x: line.at, y: line.to });
        else overlay.guide({ x: line.from, y: line.at }, { x: line.to, y: line.at });
      }
      // Every board's corners, so it is plain they can be taken hold of.
      for (const { id, box } of boards(ctx)) {
        const chosen = ctx.selection.includes(id);
        for (const corner of Object.values(cornersOf(box))) overlay.handle(corner, { filled: chosen });
      }
      if (drawing) {
        const box = between(drawing.from, drawing.to);
        overlay.box(box, { fill: true, alpha: 0.08, dashed: true });
        overlay.label({ x: box.x + box.width, y: box.y + box.height }, `${formatLength(box.width, ctx.units)} × ${formatLength(box.height, ctx.units)}`, {
          align: 'left',
          baseline: 'top',
          offset: { x: 8, y: 8 },
        });
        return;
      }
      if (held) {
        const node = ctx.node(held.id) as { width?: number; height?: number; transform?: number[] } | undefined;
        if (node?.transform && held.grip !== 'move') {
          overlay.label({ x: node.transform[4]! + (node.width ?? 0), y: node.transform[5]! + (node.height ?? 0) }, `${formatLength(node.width ?? 0, ctx.units)} × ${formatLength(node.height ?? 0, ctx.units)}`, {
            align: 'left',
            baseline: 'top',
            offset: { x: 8, y: 8 },
          });
        }
        return;
      }
      // What a click here would make: its outline, quietly, with its name under it.
      if (hover) {
        const { width, height } = size();
        const box = { x: hover.x - width / 2, y: hover.y - height / 2, width, height };
        overlay.box(box, { dashed: true, width: 1, alpha: 0.6 });
        overlay.label({ x: box.x + width / 2, y: box.y + height }, preset.name, { align: 'center', baseline: 'top', offset: { x: 0, y: 6 }, tone: 'quiet' });
      }
    },
  };
}
